import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DownloadIcon from '@assets/icons/download.svg';
import { ReactComponent as TshirtIcon } from '@assets/icons/t-shirt.svg';

import CommonImage from '@/components/CommonImage';
import PromptForm from '@/features/designTool/components/PromptForm';
import {
	useDeleteAiModelMutation,
	useGenerateAiModelMutation,
	useGetAiModelQuery,
	useSelectAiModelMutation,
	useUploadModelMutation
} from '@/features/designTool/api/designToolApi';
import LoaderComponent from '@/components/LoaderComponent';
import { toastError } from '@/components/Toast';
import { getModleImageClassName } from '@/features/designTool/utils';
import { productWiseGroupDocument } from '@/store/actions/DesignToolAction';
import { GENDER_OPTIONS, INITIAL_PROMPT_STATE, NATIONALITY_OPTIONS } from '@/features/designTool/constants';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

const VirtualTryOnSidebar = ({
	onClose,
	productId,
	frontImageUrl,
	backImageUrl,
	referenceName,
	productMarket,
	parentCatergory,
	onDownload
}) => {
	const [isShowPromptForm, setIsShowPromptForm] = useState(false);
	const [isConfirmDelete, setIsConfirmDelete] = useState(false);
	const [documentIdToDelete, setDocumentIdToDelete] = useState(null);
	const [selectedAiModelId, setSelectedAiModelId] = useState(null);
	const [selectedOptions, setSelectedOptions] = useState(INITIAL_PROMPT_STATE);
	const [description, setDescription] = useState('');

	const [aiModelList, setAiModelList] = useState([]);
	const [uploadModel, { isLoading: isUploading }] = useUploadModelMutation();
	const [generateModel, { isLoading: isGenerating, isError: isGenerateError, error: generateError }] =
		useGenerateAiModelMutation();
	const [selectAiModel, { isLoading: isSelecting }] = useSelectAiModelMutation();
	const {
		data: aiModelData = [],
		isLoading: isGettingAiModel,
		refetch
	} = useGetAiModelQuery({
		productId: productId
	});
	const [deleteAiModel, { isLoading: isDeleting }] = useDeleteAiModelMutation();
	const dispatch = useDispatch();

	useEffect(() => {
		const findGender =
			GENDER_OPTIONS.find((item) => item?.name?.toLowerCase() === productMarket?.toLowerCase()) ||
			GENDER_OPTIONS[0];

		setSelectedOptions({ ...selectedOptions, gender: findGender, nationality: NATIONALITY_OPTIONS[0] });
	}, [productMarket]);

	useEffect(() => {
		setAiModelList(aiModelData);
	}, [aiModelData]);

	useEffect(() => {
		const findDefaultSelected = aiModelData.find((item) => item?.isDefault);
		if (findDefaultSelected) setSelectedAiModelId(findDefaultSelected?.documentResponse?.id);
	}, [aiModelData]);

	useEffect(() => {
		if (isGenerateError) {
			toastError(generateError?.data?.error);
		}
	}, [isGenerateError]);

	const handleUploadCustomModel = (e) => {
		let file = e.target.files[0];
		if (file) {
			let data = {
				name: file.name,
				docMimeType: file.type,
				documentType: 'PRODUCT_VIRTUAL_TRY_ON_IMAGE',
				base64Str: '',
				bytes: [file.size]
			};
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				data.base64Str = reader.result;
				uploadModel({ productId, data })
					.unwrap()
					.then((res) => {
						if (res) {
							refetch();
						}
					})
					.catch(() => {});
			};
			reader.onerror = function (error) {
				toastError(error);
			};
		}
		e.target.value = '';
	};

	const handleOpenPromptForm = () => {
		setIsShowPromptForm(true);
	};

	const handleGenerateAIModel = async () => {
		const requestBody = {
			clothing_item_data: {
				clothing_item_name: referenceName,
				external_clothing_item_url: frontImageUrl,
				external_clothing_item_back_url: backImageUrl,
				description: description || null
			},
			num_images: 1,
			feature_setting: {
				color_hex: 'f6f5f3ff'
			},
			feature_name: 'clothing_diffusion',
			camera: selectedOptions.zoomOption?.value,
			prompt: `${selectedOptions.nationality?.value} ${selectedOptions.gender?.value}${
				selectedOptions.hairType?.value && selectedOptions.bodyType?.value
					? ` with ${selectedOptions.hairType?.value} hair and ${selectedOptions.bodyType?.value} body`
					: selectedOptions.hairType?.value
						? ` with ${selectedOptions.hairType?.value} hair`
						: selectedOptions.bodyType?.value
							? ` with ${selectedOptions.bodyType?.value} body`
							: ''
			}`
		};

		await generateModel({ data: requestBody, productId })
			.unwrap()
			.then(async (res) => {
				if (res) {
					await refetch();
					dispatch(productWiseGroupDocument(productId));
					setIsShowPromptForm(false);
					setSelectedOptions(INITIAL_PROMPT_STATE);
				}
			})
			.catch(() => {});
	};

	const resetPromptForm = () => {
		setSelectedOptions(INITIAL_PROMPT_STATE);
		setIsShowPromptForm(false);
	};

	const handleSelectAiModel = async (documentId) => {
		setSelectedAiModelId(documentId);
		await selectAiModel({ productId, documentId });
		await refetch();
		dispatch(productWiseGroupDocument(productId));
	};

	const isDisabledGenerateButton = () => {
		if (
			!selectedOptions.nationality?.value ||
			!selectedOptions.gender?.value ||
			isGenerating ||
			!selectedOptions.zoomOption?.value ||
			calculateUsedPromptCount() >= 2
		) {
			return true;
		}
		return false;
	};

	const calculateUsedPromptCount = () => {
		const usedPromptCount = aiModelData.filter((item) => !item?.isUploaded).length;
		return usedPromptCount;
	};

	const handleDeleteAiModel = async (documentId) => {
		setIsConfirmDelete(true);
		setDocumentIdToDelete(documentId);
	};

	const handleConfirmDelete = async () => {
		await deleteAiModel({ productId, documentId: documentIdToDelete })
			.unwrap()
			.then(async () => {
				await refetch();
				setSelectedAiModelId(null);
				dispatch(productWiseGroupDocument(productId));
				setIsConfirmDelete(false);
				setDocumentIdToDelete(null);
			})
			.catch(() => {});
	};

	const isUploadedAndDeleted = (item) => {
		return item?.isUploaded && item?.isDeleted;
	};

	const isAiModelAndDeleted = (item) => {
		return !item?.isUploaded && item?.isDeleted;
	};

	const isDeleted = (item) => {
		return item?.isDeleted;
	};

	const isSelectable = (item) => {
		return !isUploadedAndDeleted(item) && !isAiModelAndDeleted(item);
	};

	return (
		<>
			<LoaderComponent
				loading={isUploading || isGenerating || isSelecting || isGettingAiModel || isDeleting}
			>
				<div className={`ai-model-sidebar shadow-4dp ${isShowPromptForm ? 'full-height' : ''}`}>
					<div className="ai-model-sidebar-header border-bottom">
						<div className="title-and-close	d-flex justify-content-between align-items-center">
							<p className="regular-14 gray_dark_01">AI Model</p>
							<div className="close cursor-pointer" onClick={onClose}>
								<CommonImage src="/icons/close.svg" alt="close" weight={20} height={20} />
							</div>
						</div>
					</div>
					<div className="ai-model-sidebar-body scroll-y-label">
						{aiModelList?.length > 0 && (
							<div className="select-model-imgs-container">
								<p className="title regular-14 gray_dark_02 pt-2 mb-2">
									Select from library:
								</p>
								<div className="img-items">
									{aiModelList
										.filter((item) => !isUploadedAndDeleted(item))
										.map((item) => (
											<div
												key={item?.id}
												className={getModleImageClassName(
													item,
													selectedAiModelId,
													isSelecting
												)}
												onClick={() => {
													if (isSelectable(item)) {
														handleSelectAiModel(item?.documentResponse?.id);
													}
												}}
											>
												<CommonImage
													src={item?.documentResponse?.docUrl}
													alt="ai-model-1"
													isStatic={false}
												/>
												{isAiModelAndDeleted(item) && (
													<span className="status red-3">Deleted</span>
												)}
												<div className="download-and-close d-flex align-items-center justify-content-center">
													<div className="download-btn">
														<CommonImage
															className="cursor-pointer mr-2"
															src={DownloadIcon}
															onClick={() =>
																onDownload(
																	referenceName,
																	item?.documentResponse?.docUrl
																)
															}
															isStatic={false}
														/>
													</div>
													{!isDeleted(item) && (
														<div
															className="close-button cursor-pointer"
															onClick={(e) => {
																e.stopPropagation();
																handleDeleteAiModel(
																	item?.documentResponse?.id
																);
															}}
														>
															<CommonImage src="/icons/close.svg" alt="close" />
														</div>
													)}
												</div>
											</div>
										))}
								</div>
							</div>
						)}
						{isShowPromptForm && (
							<PromptForm
								selectedOptions={selectedOptions}
								setSelectedOptions={setSelectedOptions}
								usedPromptCount={calculateUsedPromptCount()}
								description={description}
								setDescription={setDescription}
								parentCatergory={parentCatergory}
							/>
						)}
					</div>
					<div className="ai-model-sidebar-footer">
						{isShowPromptForm ? (
							<div className="cancel-and-generate justify-content-end align-items-center">
								<button
									className="button cancel outline size32 radius-2"
									onClick={resetPromptForm}
								>
									<span className="red">Cancel</span>
								</button>
								<button
									className="button size32 radius-2 ml-2"
									onClick={handleGenerateAIModel}
									disabled={isDisabledGenerateButton()}
								>
									<span>Generate</span>
								</button>
							</div>
						) : (
							<div className="upload-presentaion-and-psd d-flex justify-content-end align-items-center">
								<div className="all-presentaion mr-3">
									<div className="inline-upload">
										<label for="customModelUpload">
											<CommonImage src="/icons/upload-dark.svg" /> Upload Custom Model
										</label>
										<input
											type="file"
											className="file-upload"
											id="customModelUpload"
											name="image"
											accept="image/*"
											multiple={false}
											onChange={handleUploadCustomModel}
										/>
									</div>
								</div>
								<button
									className="button outline size32 radius-2"
									onClick={handleOpenPromptForm}
								>
									<TshirtIcon width={20} />
									<span className="ml-1">
										{aiModelData?.length > 0
											? 'Re-Generate AI model'
											: 'Generate AI model'}
									</span>
								</button>
							</div>
						)}
					</div>
				</div>
			</LoaderComponent>
			{isConfirmDelete && (
				<DeleteConfirmationModal
					title="Delete Image"
					description="Are you sure you want to delete this image?"
					onConfirm={handleConfirmDelete}
					onClose={() => setIsConfirmDelete(false)}
				/>
			)}
		</>
	);
};

export default VirtualTryOnSidebar;
